import { Badge, Box, Flex, SegmentedControl, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import React from "react";
import { useSelector } from "react-redux";
import { LbfReceivableApi } from "../../apis";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReceivableSub } from "../../interfaces/IReceivableSub";
import { IReference } from "../../models/General";
import { tugrug } from "../../utils";
import { PageFilter } from "../layout";
import { ColumnType, ITableRef, Table } from "../table";
import RenderText from "../ui/render-text";

export function ReceivableSmeLimit() {
  const ref = React.useRef<ITableRef>(null);
  const { lbfProgramStatus } = useSelector((state: { general: IGeneral }) => state.general);
  const columns = useHeader({ lbfProgramStatus });

  const [program, setProgram] = React.useState("");
  const [programDebounced] = useDebouncedValue(program, 400);

  return (
    <>
      <PageFilter
        left={[
          <Flex justify="flex-start" align="flex-start" direction="column" wrap="wrap" key={1}>
            <Text size={14} color="#868E96">
              Хөтөлбөрийн статус
            </Text>
            <SegmentedControl
              key={1}
              onChange={(e) => setProgram(e)}
              value={program}
              data={[{ name: "Бүгд", code: "", color: "yellow" }, ...lbfProgramStatus]?.map((item: IReference, index: number) => {
                return {
                  value: item.code || "",
                  label: (
                    <Badge variant="dot" color={item.color} style={{ border: "none" }} key={`${index}`}>
                      {item.name}
                    </Badge>
                  ),
                };
              })}
            />
          </Flex>,
        ]}
      />

      <Table
        ref={ref}
        name="lbf-receivable-sme-limit.list"
        columns={columns}
        filters={{
          lbfProductType: "",
          status: programDebounced,
        }}
        loadData={(data) => LbfReceivableApi.sub(data!)}
      />
    </>
  );
}

const useHeader = ({ lbfProgramStatus }: { lbfProgramStatus: IReference[] }): ColumnType<IReceivableSub>[] => [
  {
    title: "#",
    render: (_r, index) => <RenderText text={index + 1} />,
    width: "30px",
  },
  {
    title: "CIF",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return <RenderText text={record?.clientCode || "-"} />;
    },
  },
  {
    title: "Хөтөлбөрийн нэр",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.lbfProgram?.refCode || "-"} color="#3e166e" />
          <RenderText text={record?.lbfProgram?.name || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Бүтээгдэхүүний нэр",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.lbfProgram?.lbfProduct?.refCode || "-"} color="#3e166e" />
          <RenderText text={record?.lbfProgram?.lbfProduct?.name || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Статус",
    sorter: true,
    dataIndex: "participantStatus",
    render: (record) => {
      return (
        <Badge
          size="md"
          radius="sm"
          variant="outline"
          color={(lbfProgramStatus || []).find((item: IReference) => item.code === record?.lbfProgram?.lbfProgramStatus)?.color}>
          {(lbfProgramStatus || []).find((item: IReference) => item.code === record?.lbfProgram?.lbfProgramStatus)?.name ?? "-"}
        </Badge>
      );
    },
  },
  {
    title: "Анкор худалдан авагч",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.anchorBusiness?.refCode || "-"} color="#3e166e" />
          <RenderText text={record?.anchorBusiness?.profileName || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Нийлүүлэгчийн партнер",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.partner?.refCode || "-"} color="#3e166e" />
          <RenderText text={record?.partner?.businessName || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Нийлүүлэгчийн бизнес",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.refCode || "-"} />
          <RenderText text={record?.profileName || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Баталсан лимит",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return <RenderText text={tugrug(record?.subLimit || 0)} isAmount />;
    },
  },
  {
    title: "Боломжит лимит",
    sorter: true,
    dataIndex: "availableAmount",
    render: (record) => {
      return <RenderText text={tugrug(record.availableAmount || 0)} isAmount />;
    },
  },
  {
    title: "Ашигласан лимит",
    sorter: true,
    dataIndex: "utilizedAmount",
    render: (record) => {
      return <RenderText text={tugrug(record.utilizedAmount || 0)} isAmount />;
    },
  },
  {
    title: "Хүсэлт гаргасан",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return <RenderText text={tugrug(record.pendingAmount || 0)} isAmount />;
    },
  },
  {
    title: "Олголт хүлээж буй",
    sorter: true,
    dataIndex: "toDisburseAmount",
    render: (record) => {
      return <RenderText text={tugrug(record.toDisburseAmount || 0)} isAmount />;
    },
  },
];
